"use client";

import { useState, useMemo } from "react";
import dynamic from "next/dynamic";
import { ClubFilters } from "./club-filters";
import { ClubListView } from "./club-list-view";
import type { DirectoryEntry } from "@/types";

const ClubMap = dynamic(() => import("./club-map").then((m) => m.ClubMap), {
  ssr: false,
  loading: () => (
    <div className="flex h-[500px] items-center justify-center rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900">
      <p className="text-gray-500 dark:text-gray-400">Loading map...</p>
    </div>
  ),
});

interface ClubsClientProps {
  clubs: DirectoryEntry[];
  geocodedClubs: DirectoryEntry[];
  states: string[];
  characteristics: string[];
}

export function ClubsClient({
  clubs,
  geocodedClubs,
  states,
  characteristics,
}: ClubsClientProps) {
  const [view, setView] = useState<"map" | "list">("map");
  const [search, setSearch] = useState("");
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [selectedCharacteristics, setSelectedCharacteristics] = useState<
    string[]
  >([]);

  const filteredClubs = useMemo(() => {
    return clubs.filter((club) => {
      if (
        search &&
        !club.name.toLowerCase().includes(search.toLowerCase()) &&
        !club.description.toLowerCase().includes(search.toLowerCase())
      )
        return false;
      if (selectedState && !club.tags.includes(selectedState)) return false;
      if (
        selectedCharacteristics.length > 0 &&
        !selectedCharacteristics.some((c) => club.tags.includes(c))
      )
        return false;
      return true;
    });
  }, [clubs, search, selectedState, selectedCharacteristics]);

  const filteredGeocodedClubs = useMemo(() => {
    return filteredClubs.filter(
      (c) => c.latitude !== undefined && c.longitude !== undefined
    );
  }, [filteredClubs]);

  const nonGeocodedCount = filteredClubs.length - filteredGeocodedClubs.length;

  function handleCharacteristicToggle(char: string) {
    setSelectedCharacteristics((prev) =>
      prev.includes(char) ? prev.filter((c) => c !== char) : [...prev, char]
    );
  }

  function handleClear() {
    setSearch("");
    setSelectedState(null);
    setSelectedCharacteristics([]);
  }

  return (
    <div className="space-y-6">
      {/* View toggle */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setView("map")}
          className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            view === "map"
              ? "bg-primary text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
          }`}
        >
          Map
        </button>
        <button
          onClick={() => setView("list")}
          className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            view === "list"
              ? "bg-primary text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
          }`}
        >
          List
        </button>
        <span className="ml-auto text-sm text-gray-500 dark:text-gray-400">
          {filteredClubs.length} {filteredClubs.length === 1 ? "club" : "clubs"}
        </span>
      </div>

      {/* Filters */}
      <ClubFilters
        states={states}
        characteristics={characteristics}
        search={search}
        selectedState={selectedState}
        selectedCharacteristics={selectedCharacteristics}
        onSearchChange={setSearch}
        onStateChange={setSelectedState}
        onCharacteristicToggle={handleCharacteristicToggle}
        onClear={handleClear}
      />

      {/* View */}
      {view === "map" ? (
        <>
          <ClubMap clubs={filteredGeocodedClubs} />
          {nonGeocodedCount > 0 && (
            <p className="text-center text-sm text-gray-500 dark:text-gray-400">
              {nonGeocodedCount}{" "}
              {nonGeocodedCount === 1 ? "club is" : "clubs are"} only visible in
              list view (no location data).
            </p>
          )}
        </>
      ) : (
        <ClubListView clubs={filteredClubs} />
      )}
    </div>
  );
}
